import type * as React from 'react'
import { forwardRef, useMemo } from 'react'
import type { JSX } from 'react'

import { VisuallyHidden } from '@buffer-mono/popcorn'
import { easeOut, LayoutGroup, MotionConfig } from 'motion/react'
import * as m from 'motion/react-m'

import { Mask } from './Mask'
import { NumberSection } from './NumberSection'
import type { AnimationConfig } from './types'
import { splitToSegments } from './utils/splitToSegments'
import { WholeNumber } from './WholeNumber'
import { useIsFirstRender } from '../../hooks/ui/useIsFirstRender'

/**
 * Default animation configuration for number transitions
 */
export const DEFAULT_TRANSITION: AnimationConfig = {
  opacity: { duration: 1, ease: easeOut },
  layout: { type: 'spring', duration: 1, bounce: 0 },
  y: { type: 'spring', duration: 1, bounce: 0 },
}

type AnimatedNumberProps = {
  /** The number to animate */
  value: number | bigint | string
  /** Whether to animate the whole number as one unit */
  animateAsWhole?: boolean
  /** Custom animation configuration */
  animationConfig?: Partial<AnimationConfig>
  /** Whether to animate from 0 on initial render */
  animateOnStart?: boolean
}

/**
 * A component that animates number changes with smooth transitions
 * Uses FLIP animation technique for optimal performance
 */
export const AnimatedNumber = forwardRef<
  HTMLDivElement,
  AnimatedNumberProps & { style?: React.CSSProperties }
>(
  (
    {
      value,
      style,
      animationConfig = {},
      animateAsWhole = false,
      animateOnStart = false,
      ...rest
    },
    ref,
  ): JSX.Element => {
    const { isFirstRender } = useIsFirstRender()
    const { digits, prefix } = splitToSegments(value)

    // Memoize dependency object to prevent unnecessary re-renders in motion components
    const dependency = useMemo(() => ({ value }), [value])

    // Merge default transition with custom config
    const transition = useMemo(
      () => ({
        ...DEFAULT_TRANSITION,
        ...animationConfig,
      }),
      [animationConfig],
    )

    return (
      <LayoutGroup>
        <MotionConfig transition={transition}>
          <m.div
            {...rest}
            ref={ref}
            layout
            layoutDependency={dependency}
            style={{
              lineHeight: 1,
              ...style,
              display: 'inline-flex',
              isolation: 'isolate',
              whiteSpace: 'nowrap',
            }}
          >
            <VisuallyHidden
              as="output"
              aria-live="polite"
              aria-atomic="true"
              aria-label={String(value)}
            >
              {String(value)}
            </VisuallyHidden>
            <m.div
              aria-hidden="true"
              layout
              layoutDependency={dependency}
              style={{
                display: 'inline-flex',
                direction: 'ltr',
                isolation: 'isolate',
                position: 'relative',
                zIndex: -1,
              }}
            >
              {animateAsWhole ? (
                <Mask layoutDependency={dependency}>
                  <WholeNumber
                    layoutDependency={dependency}
                    value={Number(value)}
                    initialValue={
                      isFirstRender ? (animateOnStart ? 0 : undefined) : 0
                    }
                  />
                </Mask>
              ) : (
                <>
                  <NumberSection
                    layoutDependency={dependency}
                    justify="right"
                    segments={prefix}
                    name="prefix"
                    mode="popLayout"
                  />
                  <Mask layoutDependency={dependency}>
                    <NumberSection
                      layoutDependency={dependency}
                      justify="right"
                      segments={digits}
                      name="digits"
                    />
                  </Mask>
                </>
              )}
            </m.div>
          </m.div>
        </MotionConfig>
      </LayoutGroup>
    )
  },
)

AnimatedNumber.displayName = 'AnimatedNumber'
